import { RentalVehicleType } from "../types/dtos/api/itinerary/itinerary-search-request.dto";

/** Stable UI option ids for the shared-vehicles MiniModal selector. */
export type RentalVehicleOptionId =
  | "bicycle" |
  "cargo_bicycle" |
  "moped" |
  "scooter_standing" |
  "scooter_seated" |
  "car" |
  "other";

/** All rental vehicle types enabled by default on a fresh install. */
export const ALL_RENTAL_VEHICLE_TYPES: readonly RentalVehicleType[] = [
    RentalVehicleType.BICYCLE,
    RentalVehicleType.CARGO_BICYCLE,
    RentalVehicleType.MOPED,
    RentalVehicleType.SCOOTER_STANDING,
    RentalVehicleType.SCOOTER_SEATED,
    RentalVehicleType.CAR,
    RentalVehicleType.OTHER,
] as const;

const RENTAL_VEHICLE_OPTION_ID_TO_TYPE: Record<RentalVehicleOptionId, RentalVehicleType> = {
    "bicycle":          RentalVehicleType.BICYCLE,
    "cargo_bicycle":    RentalVehicleType.CARGO_BICYCLE,
    "moped":            RentalVehicleType.MOPED,
    "scooter_standing": RentalVehicleType.SCOOTER_STANDING,
    "scooter_seated":   RentalVehicleType.SCOOTER_SEATED,
    "car":              RentalVehicleType.CAR,
    "other":            RentalVehicleType.OTHER,
};

const RENTAL_VEHICLE_TYPE_TO_OPTION_ID = Object.fromEntries( Object.entries( RENTAL_VEHICLE_OPTION_ID_TO_TYPE ).map( ( [ optionId, vehicleType ] ) => {
    return [ vehicleType, optionId ];
} ) ) as Record<RentalVehicleType, RentalVehicleOptionId>;

/**
 * Type guard for {@link RentalVehicleOptionId}.
 *
 * @param value Candidate string from the selector.
 * @returns `true` when `value` is a supported rental vehicle option id.
 */
export const isRentalVehicleOptionId = ( value: string ): value is RentalVehicleOptionId => {
    return value in RENTAL_VEHICLE_OPTION_ID_TO_TYPE;
};

/**
 * Maps selected UI option ids to API rental vehicle types.
 *
 * @param optionIds Selected checkbox option ids.
 * @returns Deduplicated rental vehicle types in selector order.
 */
export const mapOptionIdsToRentalVehicleTypes = ( optionIds: readonly string[] ): RentalVehicleType[] => {
    const mappedTypes: RentalVehicleType[] = [];

    for ( const optionId of optionIds ) {
        if ( !isRentalVehicleOptionId( optionId ) ) {
            continue;
        }

        const vehicleType = RENTAL_VEHICLE_OPTION_ID_TO_TYPE[optionId];
        if ( !mappedTypes.includes( vehicleType ) ) {
            mappedTypes.push( vehicleType );
        }
    }

    return mappedTypes;
};

/**
 * Maps persisted rental vehicle types to UI option ids for the selector value.
 *
 * @param enabledTypes Enabled rental vehicle types from persistence.
 * @returns Option ids to pass as `ImageOptionSelector` checkbox value.
 */
export const mapRentalVehicleTypesToOptionIds = ( enabledTypes: readonly RentalVehicleType[] ): readonly RentalVehicleOptionId[] => {
    return enabledTypes.
        map( ( vehicleType ) => RENTAL_VEHICLE_TYPE_TO_OPTION_ID[vehicleType] ).
        filter( ( optionId ): optionId is RentalVehicleOptionId => optionId !== undefined );
};

/**
 * Whether itinerary search should include rental routing for the given selection.
 *
 * @param enabledTypes Enabled rental vehicle types.
 * @returns `true` when at least one rental type is enabled.
 */
export const isRentalRoutingEnabled = ( enabledTypes: readonly RentalVehicleType[] ): boolean => {
    return enabledTypes.length > 0;
};
